import React, { useCallback, useEffect, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import Button from '../Button/Button';
import ThirdPane from '../ThirdPane';
import WaterServiceDetail, {
  WaterServiceStatus,
  GET_WATER_SERVICE_DETAIL,
  WaterService,
} from './WaterServiceDetail';
import WaterServiceEditModal from './WaterServiceEditModal';
import { PortalWithState } from 'react-portal';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import { Flex } from 'rebass';
import WaterServiceHistory from './WaterServiceHistory';
import useNotify from '../../hooks/useNotify';
import useMode from '../../hooks/useMode';
import useSessionInfo from '../../hooks/useSessionInfo';
import { gql } from '@/modes/waterSupplier/gql';
import RequirePerms from '@/components/RequirePerms';
import StatusChip from '@/components/Status/StatusChip';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { waterServiceStatusStyle } from '@/components/WaterService/waterServiceStatus';

// Re-export query for consumers that may need to refetch it
export { GET_WATER_SERVICE_DETAIL as GET_WATER_SERVICE };

const SET_WATER_SERVICE_STATUS = gql(`
  mutation SetWaterServiceStatus(
    $waterServiceId: ID!
    $status: WaterServiceStatus!
  ) {
    setWaterServiceStatus(waterServiceId: $waterServiceId, status: $status) {
      id
      status
    }
  }
`);

type WaterServiceThirdPaneProps = {
  waterServiceId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
};

export default function WaterServiceThirdPane({
  waterServiceId,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: WaterServiceThirdPaneProps): JSX.Element {
  const {
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery(GET_WATER_SERVICE_DETAIL, {
    skip: !waterServiceId,
    variables: { where: { id: waterServiceId || null } },
  });

  const [waterService, setWaterService] = useState<WaterService>();

  useEffect(() => {
    if (itemData && itemData?.waterServices?.length) {
      setWaterService(itemData?.waterServices[0]);
    }
  }, [waterServiceId, itemData, setWaterService]);

  if (itemError) {
    throw itemError;
  }

  const { showError, showSuccess, showWarning } = useNotify();
  const history = useHistory();
  const mode = useMode();
  const { features } = useSessionInfo();
  const hasWaterHazards = features.includes('WATER_HAZARDS');

  const [setWaterServiceStatus] = useMutation(SET_WATER_SERVICE_STATUS, {
    onError: useCallback((error: ApolloError) => {
      showError('Error setting water service status', error);
    }, []),
  });

  const isActive = waterService?.status === WaterServiceStatus.ACTIVE;

  const createHazardSurvey = useCallback(() => {
    if (waterService) {
      history.push(
        `/${mode}/hazard-surveys/new?waterServiceId=${waterService.id}`
      );
    }
  }, [waterService, history, mode]);

  const activateDeactivate = useCallback(() => {
    if (!waterService) {
      return;
    }

    const newStatus = isActive
      ? WaterServiceStatus.INACTIVE
      : WaterServiceStatus.ACTIVE;

    const actionMessage =
      newStatus === WaterServiceStatus.INACTIVE
        ? 'Are you sure you want to deactivate this service?'
        : 'Are you sure you want to reactivate this service?';

    if (!confirm(actionMessage)) {
      return;
    }

    setWaterServiceStatus({
      variables: { waterServiceId: waterService.id, status: newStatus },
      onCompleted: () => {
        if (newStatus === WaterServiceStatus.INACTIVE) {
          showWarning('Service deactivated');
        } else {
          showSuccess('Service activated');
        }
      },
    });
  }, [
    waterService,
    isActive,
    setWaterServiceStatus,
    showWarning,
    showSuccess,
    showError,
  ]);

  return (
    <ThirdPane visible={!!waterServiceId}>
      {waterService && (
        <ThirdPaneHeader
          onToggle={onToggle}
          title={
            waterService ? (
              <>
                {waterService?.siteNumber}{' '}
                <StatusChip
                  status={waterServiceStatusStyle(waterService.status)}
                >
                  {constantToLabel(waterService.status)}
                </StatusChip>
              </>
            ) : (
              'Loading...'
            )
          }
          onPrev={onPrev}
          onNext={onNext}
          prevEnabled={prevEnabled}
          nextEnabled={nextEnabled}
        >
          {hasWaterHazards && (
            <RequirePerms hazardSurveys="CREATE" waterServices="UPDATE">
              <Button
                mr={10}
                variant="primary"
                icon="plus"
                onClick={createHazardSurvey}
              >
                New Hazard Survey
              </Button>
            </RequirePerms>
          )}
          <RequirePerms waterServices="UPDATE">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <Button
                    mr={10}
                    icon="edit"
                    variant="outline"
                    onClick={openPortal}
                  >
                    Edit
                  </Button>
                  {portal(
                    <WaterServiceEditModal
                      onClose={closePortal}
                      waterService={{
                        id: waterService.id,
                        addressId: waterService.addressId,
                        meterNumber: waterService.meterNumber ?? undefined,
                        meterSize: waterService.meterSize ?? undefined,
                        type: waterService.type ?? undefined,
                        sourceType: waterService.sourceType,
                        pin: waterService.pin ?? undefined,
                        siteNumber: waterService.siteNumber,
                        radioId: waterService.radioId ?? undefined,
                        recycledWaterSiteId:
                          waterService.recycledWaterSite?.id ?? null,
                        hazardSiteId: waterService.hazardSite?.id ?? null,
                        tags: waterService.tags,
                      }}
                    />
                  )}
                </>
              )}
            </PortalWithState>
          </RequirePerms>
          <RequirePerms waterServices="UPDATE">
            <Button
              mr={10}
              variant={isActive ? 'danger' : 'success'}
              onClick={activateDeactivate}
            >
              {isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          </RequirePerms>
        </ThirdPaneHeader>
      )}
      <ThirdPaneContent>
        {waterService && (
          <Flex height="100%" flexDirection="row" flexGrow={1} flexShrink={1}>
            <Flex
              flexDirection="column"
              flexGrow={1}
              flexShrink={1}
              height="100%"
              width={720}
              overflowY="scroll"
            >
              <WaterServiceDetail
                waterService={waterService}
                onChange={refetch}
              />
            </Flex>
            <Flex
              flexDirection="column"
              flexGrow={1}
              flexShrink={1}
              height="100%"
              width={350}
              sx={{ borderLeft: '1px solid', borderLeftColor: 'tableBorder' }}
            >
              {waterServiceId && (
                <WaterServiceHistory
                  waterService={waterService}
                  refetch={refetch}
                  waterServiceId={waterServiceId}
                />
              )}
            </Flex>
          </Flex>
        )}
      </ThirdPaneContent>
    </ThirdPane>
  );
}
